/* Cart empty products
   Loads the products shown in the empty cart (drawer and cart page) from the
   Section Rendering API: sections/cart-empty-products.liquid rendered in the
   context of /collections/<handle>?sort_by=<sort>, so the collection arrives
   already sorted (best-selling by default). The placeholder element comes from
   snippets/cart-empty-products.liquid with the URL in `section-url`.
   - In the drawer the request waits until the drawer opens (on:modal:opening);
     on the cart page it runs on connect.
   - Responses are cached per URL for the page lifetime, so re-rendering the
     empty state (e.g. after removing the last item) does not refetch.
   - GA4: pushes view_item_list when the list is injected and select_item when a
     card link or button is clicked (items come from data-dl-items). */
const responses = new Map();

class CartEmptyProducts extends HTMLElement {
    #loaded = false;
    #boundOpen;
    #modal;
    connectedCallback() {
        this.url = this.getAttribute("section-url");
        if (!this.url) return;
        this.#modal = this.closest("modal-dialog");
        if (this.#modal && this.#modal.getAttribute("aria-hidden") === "true") {
            this.#boundOpen = () => this.#load();
            this.#modal.addEventListener("on:modal:opening", this.#boundOpen, {once: true});
        } else {
            this.#load();
        }
    }
    disconnectedCallback() {
        if (this.#modal && this.#boundOpen) {
            this.#modal.removeEventListener("on:modal:opening", this.#boundOpen);
        }
    }
    async #load() {
        if (this.#loaded) return;
        this.#loaded = true;
        try {
            if (!responses.has(this.url)) {
                responses.set(this.url, fetch(this.url, {headers: {Accept: "text/html"}}).then((response => response.ok ? response.text() : "")));
            }
            const html = await responses.get(this.url);
            if (!html || !this.isConnected) return;
            const content = new DOMParser().parseFromString(html, "text/html").querySelector("[data-cart-empty-products]");
            if (!content || !content.querySelector(".product-card")) return;
            if (this.closest(".cart--page")) {
                // Cart page: 2 cards per row like the page recommendations, and no drawer
                // notification on add (the page cart re-renders itself from the store).
                content.querySelectorAll("[data-items]").forEach((grid => grid.classList.replace("grid-layout--1", "grid-layout--2")));
                content.querySelectorAll("[notify-on-add]").forEach((element => element.removeAttribute("notify-on-add")));
            }
            if (!customElements.get("items-scroll")) {
                await import("items-scroll").catch((() => {}));
            }
            if (!this.isConnected) return;
            this.replaceChildren(content);
            this.removeAttribute("hidden");
            this.#track(content);
        } catch (error) {
            // The empty cart keeps its default content.
        }
    }
    #track(content) {
        let items = [];
        try {
            items = JSON.parse(content.getAttribute("data-dl-items") || "[]");
        } catch (error) {
            return;
        }
        if (!Array.isArray(items) || items.length === 0) return;
        const listName = content.getAttribute("data-list-name") || "";
        const listId = content.getAttribute("data-list-id") || "cart_empty_products";
        const pageType = this.closest(".cart--page") ? "Cart" : "Cart drawer";
        const push = (event, eventItems) => {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ecommerce: null});
            window.dataLayer.push({
                event: event,
                pageType: pageType,
                productList: listName,
                ecommerce: {item_list_id: listId, item_list_name: listName, items: eventItems}
            });
        };
        push("view_item_list", items);
        content.addEventListener("click", (event => {
            const card = event.target.closest("[data-product-id]");
            if (!card || !event.target.closest("a, button")) return;
            const item = items.find((i => String(i.item_id) === card.getAttribute("data-product-id")));
            if (item) push("select_item", [item]);
        }));
    }
}

customElements.define("cart-empty-products", CartEmptyProducts);
